/** @jsx jsx */
import { jsx } from "theme-ui";
import React from "react";
import { Text } from "@shared-components/Text/Text";
import { Box } from "@shared-components/Box/Box";
import { Heading } from "@shared-components/Heading/Heading";
import { Container } from "@shared-components/Container/Container";
import { ButtonLink } from "@shared-components/Button/Button";
import {
  HeroStyle,
  HeroContentStyle,
  scrollDownStyles,
  scrollDownWrapStyles,
  headingStyles,
  descriptionStyles,
  scrollDownArrowStyles,
} from "./styles";
import { HeroModule } from "../../carlsberg-headless/types/modules/Hero";
import { Link as ScrollLink } from "react-scroll";
import { LAYOUT_PAGE_NAV_POINT_PREFIX } from "../../shared/constants/constants";
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
import { useSiteContext } from "../../context/site/useSiteContext";
import ScrollDownIcon from "@svg-icons/scroll-down-icon.component.svg";
import { TAXONOMIES_MAP } from "../../carlsberg-headless/taxonomies-map";
import { THEMES_MAP } from "../../carlsberg-headless/themes-map";
import { LinkCollectionLink } from "../../carlsberg-headless/types/LinkCollection";
import { Nullable } from "../../shared/types/util";
import { OpeningHour } from "../../carlsberg-headless/types/OpeningHour";
import { motion } from "framer-motion";
import { UmbracoParallaxBackgroundImage } from "@shared-components/Image/UmbracoParallaxImage/UmbracoParallaxImage";
import { BackgroundParallaxVideo } from "@shared-components/BackgroundVideo/BackgroundParallaxVideo/BackgroundParallaxVideo";
import TicketIcon from "@svg-icons/ticket.component.svg";
import { Icon } from "@shared-components/Icon/Icon";
import { buttonTextStyles } from "@shared-components/Button/styles";

export type HeroContentProps = HeroProps;

const HeroContent: React.FC<HeroContentProps> = ({
  title,
  description,
  link,
  cultureScrollDown,
  taxonomies,
  themes,
  bookTicketLink,
  showBookTicket,
}) => {
  return (
    <Box
      id="hero-content"
      sx={{
        ...HeroContentStyle,
        ...(themes?.includes(
          THEMES_MAP.hero_module.hero_module_transparent_background_overlay
        ) && {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
        }),
      }}
    >
      <Container>
        {title && (
          <Heading sx={headingStyles} as="h1">
            {title}
          </Heading>
        )}
        {description && (
          <Text as="p" sx={descriptionStyles}>
            {description}
          </Text>
        )}
        {link && (
          <ButtonLink
            to={link.url}
            target={link.target ?? ""}
            sx={{
              mt: [8, 10, 14],
            }}
            {...(themes?.includes(
              THEMES_MAP.hero_module.hero_module_button_background_white
            )
              ? { variant: "white" }
              : { variant: "whiteOutline", outline: true })}
          >
            {link.name}
          </ButtonLink>
        )}
      </Container>
      {!link && showBookTicket && bookTicketLink?.link?.url && (
        <ButtonLink
          to={bookTicketLink.link.url}
          target={bookTicketLink.link.target ? bookTicketLink.link.target : ""}
          variant="whiteOutline"
          outline
          sx={{
            display: ["flex", "flex", "none"],
            mt: [8, 10, 14],
            columnGap: "16px",
            py: ["14px", null],
            px: ["14px", null],
            span: {
              minWidth: "auto",
            },
            "svg path.ticket_component_svg__flawer": {
              fill: "white  ",
            },
            "&:hover, &:focus": {
              "svg path": {
                stroke: "black",
                fill: "none",
                "&.ticket_component_svg__flawer": {
                  fill: "black",
                },
              },
            },
          }}
        >
          <Box as="span">
            <Icon
              sx={{
                strokeWidth: "1.5px",
                stroke: "white",
                fill: "none",
                width: "24px",
                height: "24px",
              }}
              svg={TicketIcon}
            />
          </Box>
          <Box as="span" sx={{ ...buttonTextStyles }}>
            {bookTicketLink.link.name}
          </Box>
        </ButtonLink>
      )}
      {!taxonomies?.includes(TAXONOMIES_MAP.hero_module.hide_scroll_down) && (
        <Box
          sx={{
            ...scrollDownWrapStyles,
          }}
        >
          <ScrollLink
            sx={scrollDownStyles}
            to={LAYOUT_PAGE_NAV_POINT_PREFIX}
            duration={500}
            smooth
          >
            {cultureScrollDown ?? cultureScrollDown}
            <motion.span
              sx={scrollDownArrowStyles}
              animate={{
                y: 15,
                transition: {
                  easings: "easeInOut",
                  duration: 0.6,
                  yoyo: "Infinity",
                },
              }}
            >
              <ScrollDownIcon />
            </motion.span>
          </ScrollLink>
        </Box>
      )}
    </Box>
  );
};

export interface HeroProps extends HeroModule {
  cultureScrollDown?: string;
  cultureOpen?: string;
  bookTicketLink?: LinkCollectionLink;
  openingHours?: Nullable<OpeningHour[]>;
  showBookTicket?: boolean;
}

const PureHero: React.FC<HeroProps> = (props) => {
  return (
    <Box
      key={props.cbKey}
      sx={{
        ...HeroStyle,
      }}
    >
      {props?.video?.fullUrl && (
        <BackgroundParallaxVideo src={props.video.fullUrl} parallaxY={300}>
          <HeroContent {...props} />
        </BackgroundParallaxVideo>
      )}
      {!props.video && props?.image?.fullUrl && (
        <UmbracoParallaxBackgroundImage
          image={props.image}
          height={[650, 1050, 1440]}
          lazyLoadOffset={0}
          parallaxY={300}
        >
          <HeroContent {...props} />
        </UmbracoParallaxBackgroundImage>
      )}
      {!props.video && !props.image && <HeroContent {...props} />}
    </Box>
  );
};

const Hero: React.FC<HeroProps> = (props) => {
  const { get } = useDictionaryContext();
  const { currentCulture, bookTicketLink, pageTaxonomies } = useSiteContext();

  return (
    <PureHero
      bookTicketLink={bookTicketLink?.links?.[0]}
      cultureOpen={get(DICTIONARY_MAP.opening_hours.open, currentCulture)}
      cultureScrollDown={get(DICTIONARY_MAP.scroll_down, currentCulture)}
      showBookTicket={pageTaxonomies.includes(
        TAXONOMIES_MAP.page.page_show_book_ticket_mobile
      )}
      {...props}
    />
  );
};

export { Hero, PureHero };
